import React from "react";
import PropTypes from "prop-types";
import { ArrowSquareOut } from "@phosphor-icons/react";
import {
  buildPathWithAIPaneTab,
} from "../../../utils/aiPaneUrl";
import {
  InboxAIHeaderEmpty,
  InboxAIHeaderMetaRow,
  InboxAIHeaderOpenInMatterLink,
  InboxAIHeaderStatusChip,
  InboxAIHeaderSubtitle,
  InboxAIHeaderTitle,
  InboxAIHeaderTitleRow,
  InboxAIHeaderWrapper,
} from "../InboxStyled";

/**
 * Header that sits above `<AIPane mode="inbox">` in the right rail.
 *
 * Surfaces the per-row context that the matter-workspace AIPane gets
 * "for free" from its URL/breadcrumb chain: doc name, matter context,
 * and a one-click escape hatch into the matter workspace.
 *
 * "Open in matter" navigates to the matter detail page for the
 * selected doc with the AI panel pre-opened on the Briefing tab —
 * matches the user's intent ("I'm reviewing this in the inbox; show
 * me the rest of the matter without losing context"). The
 * `buildPathWithAIPaneTab` helper appends `?ai=briefing` rather than
 * inheriting whatever inbox-side tab the user was on (they could be
 * on `?ai=context-analysis`, which doesn't exist in matter mode and
 * would clamp to briefing anyway).
 *
 * Document path uses the same `D{id}` prefix the matter workspace
 * uses for its own URL routing — see `getActiveDocumentIdFromPath`
 * in `aiPaneUrl.js` for the inverse parser.
 *
 * Rendered as a real anchor with `target="_blank"` (rather than an
 * `onClick` + `navigate()` button) so it opens the matter workspace
 * in a new tab and keeps the inbox triage list intact in this one —
 * this also gives ctrl/cmd-click and middle-click the native
 * "open in new tab" behavior for free.
 *
 * Open/Closed sits on its own row with the case manager (top-right)
 * so the Brief panel drops a line rather than crowding the client
 * name (Matthew, 2026-09-14).
 */
export const matterStatusTone = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return null;
  const key = value.toLowerCase();
  if (key === "open") return "open";
  if (key === "closed") return "closed";
  return "other";
};

const InboxAIHeader = ({ item = null }) => {
  if (!item) {
    return (
      <InboxAIHeaderWrapper>
        <InboxAIHeaderEmpty>
          Select a document to see its briefing and context analysis.
        </InboxAIHeaderEmpty>
      </InboxAIHeaderWrapper>
    );
  }

  const matter = item.matter || null;
  const canOpenInMatter = Boolean(matter?.id);
  const openInMatterHref = canOpenInMatter
    ? buildPathWithAIPaneTab(`/matter/${matter.id}/D${item.id}`, "briefing")
    : null;
  const statusLabel = String(matter?.status || "").trim();
  const statusTone = matterStatusTone(statusLabel);
  const showMetaRow = Boolean(statusTone || matter?.case_manager);

  return (
    <InboxAIHeaderWrapper>
      <InboxAIHeaderTitleRow>
        <InboxAIHeaderTitle title={item.name || ""}>
          {item.name || "Untitled document"}
        </InboxAIHeaderTitle>
        {canOpenInMatter && (
          <InboxAIHeaderOpenInMatterLink
            href={openInMatterHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${item.name || "document"} in matter workspace (new tab)`}
          >
            Open in matter
            <ArrowSquareOut size={14} weight="bold" />
          </InboxAIHeaderOpenInMatterLink>
        )}
      </InboxAIHeaderTitleRow>
      {matter ? (
        <>
          <InboxAIHeaderSubtitle>
            <span className="matter-info">
              <span className="matter-name">{matter.name || "Unassigned matter"}</span>
              {matter.matter_number ? (
                <>
                  {" · "}
                  <span className="matter-number">{matter.matter_number}</span>
                </>
              ) : null}
            </span>
          </InboxAIHeaderSubtitle>
          {showMetaRow ? (
            <InboxAIHeaderMetaRow>
              {statusTone ? (
                <InboxAIHeaderStatusChip
                  $tone={statusTone}
                  aria-label={`Matter is ${statusLabel}`}
                >
                  {statusLabel}
                </InboxAIHeaderStatusChip>
              ) : null}
              {matter.case_manager ? (
                <span className="matter-case-manager">
                  Case manager: {matter.case_manager}
                </span>
              ) : null}
            </InboxAIHeaderMetaRow>
          ) : null}
        </>
      ) : (
        <InboxAIHeaderSubtitle>
          <span>Not yet assigned to a matter</span>
        </InboxAIHeaderSubtitle>
      )}
    </InboxAIHeaderWrapper>
  );
};

InboxAIHeader.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    matter: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      matter_number: PropTypes.string,
      status: PropTypes.string,
      case_manager: PropTypes.string,
    }),
  }),
};

export default InboxAIHeader;
